import { ReactNode, useCallback, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  authSucceeded,
  bootstrapFailed,
  bootstrapStarted,
  loggedOut,
  sessionExpired,
} from "../redux/slices/Auth";
import { authApi } from "./authApi";
import { authStorage } from "./authStorage";
import { toAuthError } from "./authErrors";
import { initiateSocketIo } from "../helpers/socketEvents";
import { updateFeedback } from "../redux/slices/AppState";

export const AuthBootstrap = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { status, error } = useSelector((state: RootState) => state.Auth);

  const restoreSession = useCallback(async () => {
    if (!authStorage.getToken()) {
      dispatch(loggedOut());
      return;
    }

    dispatch(bootstrapStarted());
    try {
      const session = await authApi.getSession();
      authStorage.saveSession(session);
      dispatch(authSucceeded(session.user));
      if (session.user.canPlaceOrder) {
        await initiateSocketIo(true, dispatch).catch(() => undefined);
      }
    } catch (cause) {
      const authError = toAuthError(cause);
      if (
        authError.code === "SESSION_INVALID" ||
        authError.code === "ACCOUNT_INACTIVE"
      ) {
        authStorage.clearSession();
        dispatch(
          updateFeedback({
            show: true,
            type: "error",
            message: authError.message,
          })
        );
        dispatch(sessionExpired());
      } else {
        dispatch(bootstrapFailed(authError));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (status === "initializing") restoreSession();
  }, [restoreSession, status]);

  if (status === "initializing") {
    return (
      <Box
        minHeight="100vh"
        display="grid"
        sx={{ placeItems: "center", bgcolor: "#0d0a0b" }}
      >
        <Box textAlign="center">
          <CircularProgress color="primary" />
          <Typography color="white" mt={2}>
            Restoring your workspace…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === "bootstrap-failed") {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="#0d0a0b"
        p={3}
      >
        <Box width="100%" maxWidth={480}>
          <Typography variant="h5" color="white" mb={2}>
            Unable to restore your session
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message || "The authentication service is unavailable."}
          </Alert>
          <Button variant="contained" onClick={restoreSession} sx={{ mr: 1 }}>
            Retry
          </Button>
          <Button
            variant="text"
            onClick={() => {
              authStorage.clearSession();
              dispatch(loggedOut());
            }}
          >
            Sign in again
          </Button>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};
